import { drawFrame, mapToPlot, drawSpatialFieldRgb } from './canvasUtils';

export const fisher1D = {
    init: (w) => ({ u: Array.from({ length: w }, (_, i) => (i < 40 ? 1 : 0)), t: 0 }),
    step: (ctx, w, h, state, params) => {
        const D = params.diffusion;
        const next = state.u.map((val, i) => {
            const lap = (state.u[i - 1] ?? val) + (state.u[i + 1] ?? val) - 2 * val;
            const reaction = params.growth * val * (1 - val);
            return Math.max(0, Math.min(1, val + params.dt * (D * lap + reaction)));
        });
        state.u = next;
        state.t += params.dt;

        const area = drawFrame(ctx, w, h, {
            xLabel: 'x (space)',
            yLabel: 'u(x, t)',
            title: '1D Fisher-KPP front',
            t: state.t,
        });

        const n = state.u.length;
        ctx.strokeStyle = '#2cbb5d';
        ctx.lineWidth = 2;
        ctx.beginPath();
        state.u.forEach((val, i) => {
            const mapped = mapToPlot(area, i, val, 0, n - 1, 0, 1);
            if (i === 0) ctx.moveTo(mapped.x, mapped.y);
            else ctx.lineTo(mapped.x, mapped.y);
        });
        ctx.stroke();
    },
};

export const fisher2D = {
    init: (w) => {
        const n = 100;
        const u = new Float32Array(n * n);
        const cx = n / 2;
        const cy = n / 2;
        for (let y = 0; y < n; y++) {
            for (let x = 0; x < n; x++) {
                u[y * n + x] = Math.hypot(x - cx, y - cy) < 4 ? 1 : 0;
            }
        }
        return { n, u, t: 0 };
    },
    step: (ctx, w, h, state, params) => {
        const { n, u } = state;
        const next = new Float32Array(n * n);
        const D = params.diffusion;
        for (let y = 0; y < n; y++) {
            for (let x = 0; x < n; x++) {
                const idx = y * n + x;
                const lap =
                    u[y * n + ((x - 1 + n) % n)] +
                    u[y * n + ((x + 1) % n)] +
                    u[((y - 1 + n) % n) * n + x] +
                    u[((y + 1) % n) * n + x] -
                    4 * u[idx];
                const reaction = params.growth * u[idx] * (1 - u[idx]);
                next[idx] = Math.max(0, Math.min(1, u[idx] + params.dt * (D * lap + reaction)));
            }
        }
        state.u = next;
        state.t += params.dt;

        drawSpatialFieldRgb(ctx, w, h, n, (idx) => {
            const val = next[idx];
            const g = Math.floor(val * 220);
            return `rgb(${Math.floor(val * 40)},${g + 30},${Math.floor(val * 90)})`;
        }, {
            xLabel: 'x (space)',
            yLabel: 'y (space)',
            title: 'Population density u(x, y, t)',
            t: state.t,
        });
    },
};

export const fisherStochastic = {
    init: (w) => ({ u: Array.from({ length: w }, (_, i) => (i < 40 ? 1 : 0)), t: 0 }),
    step: (ctx, w, h, state, params) => {
        const D = params.diffusion;
        const noiseAmp = params.noise;
        const next = state.u.map((val, i) => {
            const lap = (state.u[i - 1] ?? val) + (state.u[i + 1] ?? val) - 2 * val;
            const reaction = params.growth * val * (1 - val);
            const noise = noiseAmp * (Math.random() - 0.5) * Math.sqrt(val * (1 - val) + 1e-3);
            return Math.max(0, Math.min(1, val + params.dt * (D * lap + reaction) + noise));
        });
        state.u = next;
        state.t += params.dt;

        const area = drawFrame(ctx, w, h, {
            xLabel: 'x (space)',
            yLabel: 'u(x, t)',
            title: 'Stochastic Fisher front',
            t: state.t,
        });

        const n = state.u.length;
        ctx.strokeStyle = '#ef4743';
        ctx.lineWidth = 2;
        ctx.beginPath();
        state.u.forEach((val, i) => {
            const mapped = mapToPlot(area, i, val, 0, n - 1, 0, 1);
            if (i === 0) ctx.moveTo(mapped.x, mapped.y);
            else ctx.lineTo(mapped.x, mapped.y);
        });
        ctx.stroke();
    },
};
